import { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

export default function ConnectWhatsApp() {
  const { user, selectedBusiness } = useAuth();
  const [status, setStatus] = useState('idle');
  const [msg, setMsg] = useState('');
  const [manual, setManual] = useState({ waba_id: '', access_token: '', phone_number_id: '' });
  const [showManual, setShowManual] = useState(false);
  const [connected, setConnected] = useState(false);
  const [profile, setProfile] = useState(null);
  const [verifyStatus, setVerifyStatus] = useState('idle');
  const [verifyMsg, setVerifyMsg] = useState('');
  const [profileForm, setProfileForm] = useState({ description: '', address: '', email: '', website: '' });
  const [profileMsg, setProfileMsg] = useState('');
  const [siblings, setSiblings] = useState([]);
  const [disconnected, setDisconnected] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteMsg, setDeleteMsg] = useState('');
  const [rowBusy, setRowBusy] = useState(null);

  const FB_APP_ID = '1430190582144691';
  const bid = selectedBusiness?.id || selectedBusiness?.business_id || user?.business_id;

  useEffect(() => {
    if (bid) fetchProfile();
    fetchSiblings();
  }, [bid]);

  const fetchSiblings = async () => {
    try {
      const r = await api.get('/businesses/siblings');
      setSiblings(Array.isArray(r.data?.connected) ? r.data.connected : []);
      setDisconnected(Array.isArray(r.data?.disconnected) ? r.data.disconnected : []);
    } catch(e) {}
  };

  const doDisconnect = async (id) => {
    setRowBusy(id);
    try { await api.post(`/businesses/${id}/deactivate`); await fetchSiblings(); }
    catch(e) { setDeleteMsg('\u274c ' + (e.response?.data?.error || e.message)); }
    finally { setRowBusy(null); }
  };

  const doReconnect = async (id) => {
    setRowBusy(id);
    try { await api.post(`/businesses/${id}/reconnect`); await fetchSiblings(); }
    catch(e) { setDeleteMsg('\u274c ' + (e.response?.data?.error || e.message)); }
    finally { setRowBusy(null); }
  };

  const doDeletePermanent = async () => {
    if (!deleteTarget) return;
    setDeleteMsg('');
    try {
      await api.post(`/businesses/${deleteTarget.id}/delete-permanent`, { confirm_name: deleteConfirmText });
      setDeleteTarget(null);
      setDeleteConfirmText('');
      await fetchSiblings();
    } catch(e) {
      setDeleteMsg('\u274c ' + (e.response?.data?.error || e.message));
    }
  };

  // FB SDK is now initialized globally in Layout.jsx

  const fetchProfile = async () => {
    try {
      const r = await api.get('/connect/profile' + (bid ? '?business_id=' + bid : ''));
      if (r.data && r.data.verified_name) {
        setProfile(r.data);
        setConnected(true);
        setProfileForm({
          description: r.data.description || '',
          address: r.data.address || '',
          email: r.data.email || '',
          website: r.data.websites?.[0] || ''
        });
      }
    } catch(e) {}
  };

  const saveProfile = async () => {
    setProfileMsg('');
    try {
      await api.post('/connect/profile', { ...profileForm, business_id: bid });
      setProfileMsg('\u2705 Profile saved!');
      fetchProfile();
    } catch(e) {
      setProfileMsg('\u274c Failed: ' + (e.response?.data?.error || e.message));
    }
  };

  const launchEmbeddedSignup = () => {
    setStatus('loading'); setMsg('');
    if (!window.FB) { setMsg('Facebook SDK not loaded.'); setStatus('error'); return; }
    window.FB.login((response) => {
      (async () => {
        if (response.authResponse?.code) {
          try {
            const r = await api.post('/connect/whatsapp', { code: response.authResponse.code, is_add_account: true, session_info: response.authResponse.session_info || {} });
            if (r.data.success) {
              setStatus('success'); setMsg(`\u2705 WhatsApp Connected! WABA ID: ${r.data.waba_id}`);
              window.location.reload();
              return;
            }
          } catch(e) { setStatus('error'); setMsg('\u274c Connection failed: ' + (e.response?.data?.error || e.message)); }
        } else { setStatus('error'); setMsg('\u274c Login cancelled or failed'); }
      })();
    }, { config_id: '26070060269330483', response_type: 'code', override_default_response_type: true, scope: 'whatsapp_business_management,whatsapp_business_messaging,public_profile', extras: { setup: {}, featureType: '', sessionInfoVersion: '3' } });
  };

  const saveManual = async () => {
    if (!manual.waba_id || !manual.access_token || !manual.phone_number_id) { setMsg('\u274c All fields required'); return; }
    setStatus('loading');
    try {
      await api.post('/connect/manual', { ...manual, business_id: bid });
      setStatus('success'); setMsg('\u2705 WhatsApp Connected Successfully!'); fetchProfile();
    } catch(e) { setStatus('error'); setMsg('\u274c Failed: ' + (e.response?.data?.error || e.message)); }
  };

  const S = {
    page: { padding: 32, fontFamily: 'DM Sans, sans-serif', maxWidth: 600 },
    card: { background: '#fff', borderRadius: 16, padding: '32px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginBottom: 24 },
    inp: { width: '100%', padding: '10px 14px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', marginBottom: 12 },
    btn: { width: '100%', padding: '13px', borderRadius: 10, border: 'none', fontWeight: 700, fontSize: 15, cursor: 'pointer' },
    label: { fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' },
  };

  return (
    <div style={S.page}>
      <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>Connect WhatsApp</h2>
      <p style={{ color: '#6b7280', marginBottom: 24, fontSize: 14 }}>Connect your WhatsApp Business account to start sending messages.</p>

      {siblings.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
            Connected accounts ({siblings.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {siblings.map(s => (
              <div key={s.id} style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#166534' }}>
                    {s.id === bid ? '\u2705 ' : ''}{s.name || `Business ${s.id}`}
                  </div>
                  <div style={{ fontSize: 12, color: '#166534', marginTop: 2 }}>
                    {(s.id === bid && profile?.display_phone_number) ? profile.display_phone_number : (s.display_phone_number || '')}
                  </div>
                  {s.id === bid && (
                    <span style={{ fontSize: 12, fontWeight: 700, color: profile?.status === 'CONNECTED' ? '#166534' : '#d97706' }}>
                      {profile?.status === 'CONNECTED' ? 'Verified' : 'Connecting...'}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => doDisconnect(s.id)} disabled={rowBusy === s.id}
                    style={{ fontSize: 12, padding: '5px 10px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer' }}>
                    {rowBusy === s.id ? '...' : 'Disconnect'}
                  </button>
                  <button onClick={() => { setDeleteTarget(s); setDeleteConfirmText(''); setDeleteMsg(''); }}
                    style={{ fontSize: 12, padding: '5px 10px', borderRadius: 6, border: '1px solid #fecaca', background: '#fff', color: '#dc2626', cursor: 'pointer' }}>
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {disconnected.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
            Disconnected
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {disconnected.map(s => (
              <div key={s.id} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 14, color: '#374151' }}>{s.name || `Business ${s.id}`}</div>
                  <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{s.display_phone_number || ''}</div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => doReconnect(s.id)} disabled={rowBusy === s.id}
                    style={{ fontSize: 12, padding: '5px 10px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer' }}>
                    {rowBusy === s.id ? '...' : 'Reconnect'}
                  </button>
                  <button onClick={() => { setDeleteTarget(s); setDeleteConfirmText(''); setDeleteMsg(''); }}
                    style={{ fontSize: 12, padding: '5px 10px', borderRadius: 6, border: '1px solid #fecaca', background: '#fff', color: '#dc2626', cursor: 'pointer' }}>
                    Delete permanently
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {deleteTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, width: 320 }}>
            <p style={{ fontSize: 15, fontWeight: 700, margin: '0 0 6px' }}>Delete {deleteTarget.name} permanently?</p>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 12px', lineHeight: 1.5 }}>
              This can't be undone. Type the business name to confirm.
            </p>
            <input style={S.inp} placeholder={deleteTarget.name} value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)} />
            {deleteMsg && <p style={{ fontSize: 12, color: '#dc2626', margin: '0 0 8px' }}>{deleteMsg}</p>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => { setDeleteTarget(null); setDeleteConfirmText(''); setDeleteMsg(''); }}
                style={{ fontSize: 13, padding: '6px 12px', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={doDeletePermanent} disabled={deleteConfirmText.trim() !== deleteTarget.name}
                style={{ fontSize: 13, padding: '6px 12px', borderRadius: 6, border: 'none', background: deleteConfirmText.trim() === deleteTarget.name ? '#dc2626' : '#fca5a5', color: '#fff', cursor: deleteConfirmText.trim() === deleteTarget.name ? 'pointer' : 'not-allowed' }}>
                Delete permanently
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={S.card}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>🚀 {siblings.length > 0 ? 'Add another account' : 'One-Click Setup'}</div>
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>
          {siblings.length > 0
            ? 'Log in with Facebook to link a new WhatsApp Business number to this organization.'
            : 'Login with Facebook and connect your WhatsApp Business account automatically.'}
        </p>
        <button onClick={launchEmbeddedSignup} disabled={status === 'loading'} style={{ ...S.btn, background: '#1877F2', color: '#fff' }}>
          {status === 'loading' ? '\u23F3 Connecting...' : '\uD83D\uDD35 Connect with Facebook'}
        </button>
      </div>

      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>⚙️ Manual Setup</div>
          <button onClick={() => setShowManual(!showManual)} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 13 }}>
            {showManual ? 'Hide' : 'Show'}
          </button>
        </div>
        {showManual && (
          <div>
            <input style={S.inp} placeholder="WABA ID" value={manual.waba_id} onChange={e => setManual({...manual, waba_id: e.target.value})} />
            <input style={S.inp} placeholder="Phone Number ID" value={manual.phone_number_id} onChange={e => setManual({...manual, phone_number_id: e.target.value})} />
            <textarea style={{ ...S.inp, height: 80, resize: 'vertical' }} placeholder="Access Token (EAAxxxx...)" value={manual.access_token} onChange={e => setManual({...manual, access_token: e.target.value})} />
            <button onClick={saveManual} disabled={status === 'loading'} style={{ ...S.btn, background: '#25D366', color: '#fff' }}>
              {status === 'loading' ? '\u23F3 Saving...' : '\uD83D\uDCBE Save Credentials'}
            </button>
          </div>
        )}
      </div>

      {msg && (
        <div style={{ padding: '14px 18px', borderRadius: 10, fontSize: 14, fontWeight: 600,
          background: status === 'success' ? '#f0fdf4' : '#fef2f2',
          color: status === 'success' ? '#166534' : '#dc2626',
          border: `1px solid ${status === 'success' ? '#bbf7d0' : '#fecaca'}` }}>
          {msg}
        </div>
      )}
    </div>
  );
}
